import { env } from "@/config/env";
import { reportAuthEvent } from "@/lib/authDiagnostics";
import { authSession } from "@/utils/authSession";
import {
  featuresService,
  haveFeaturesChanged,
} from "@/services/featuresService";
import {
  discardLegacySession,
  usesWebClient,
  WEB_CLIENT_HEADER,
  WEB_CLIENT_VALUE,
} from "@/utils/webClient";
import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { jwtDecode } from "jwt-decode";

export const serverSetAccessToken = async (
  token: string,
  refreshToken?: string,
  session?: unknown
) => {
  if (token) {
    localStorage.setItem("access_token", token);
    if (refreshToken) {
      localStorage.setItem("refresh_token", refreshToken);
    }
  } else {
    // Modo cookie: resposta sem token, nada de credencial no JavaScript.
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  }
  authSession.store(session);
};

/**
 * Encerra a sessão local: limpa os tokens e recarrega. `reason` identifica
 * QUEM decidiu encerrar (logout do usuário, refresh inválido no interceptor,
 * refresh inválido no SSE…) — é o evento central do diagnóstico de "deslogou
 * sozinho", enviado ao PostHog antes do reload apagar a página.
 */
export const serverRemoveAccessToken = async (
  reason: string = "unspecified",
  details: Record<string, unknown> = {}
) => {
  reportAuthEvent(
    "auth_session_ended",
    { reason, ...details },
    { sendInstantly: true }
  );

  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  authSession.clear();
  window.location.reload();
};

export type ErrorApi = {
  name: string;
  message: [string];
  action: string;
  status_code: number;
  /**
   * Discriminador opcional do 401. "stale_token" = o claim
   * permissions_version do access token divergiu da versão corrente do
   * usuário (permissões mudaram ou usuário foi desativado); o fluxo padrão
   * do interceptor abaixo (refresh silencioso + retry, com reload quando as
   * features mudam) resolve sem deslogar.
   */
  code?: string;
};

type RequestWithMarker = InternalAxiosRequestConfig & {
  _credentialMarker?: string;
};

const api = axios.create({
  baseURL: `${env.API_URL}`,
});

console.log(env.API_URL);

/**
 * Erro de renovação de token. `shouldLogout` indica que o refresh token é
 * definitivamente inválido (401/403 ou ausência de tokens) — só nesse caso a
 * sessão deve ser encerrada. Falhas transitórias (rede, 5xx) NÃO deslogam.
 * `status` guarda o HTTP da chamada de refresh (undefined = sem resposta,
 * ex.: rede) para o diagnóstico.
 */
export class RefreshError extends Error {
  shouldLogout: boolean;
  status?: number;
  constructor(message: string, shouldLogout: boolean, status?: number) {
    super(message);
    this.name = "RefreshError";
    this.shouldLogout = shouldLogout;
    this.status = status;
  }
}

const decodeExp = (token: string): number | null => {
  try {
    // jwtDecode trata base64url corretamente (atob falha com '-'/'_').
    const { exp } = jwtDecode(token);
    return typeof exp === "number" ? exp : null;
  } catch {
    return null;
  }
};

// Considera expirado (com 5s de folga) ou indecodificável.
const isExpired = (token: string): boolean => {
  const exp = decodeExp(token);
  if (exp === null) return true;
  return Date.now() >= exp * 1000 - 5000;
};

// Promise de refresh em andamento NESTA aba: qualquer chamada concorrente
// aguarda a mesma renovação em vez de disparar outra (single-flight).
let refreshPromise: Promise<string> | null = null;

/**
 * Renovação efetiva do token. Roda sob `navigator.locks` para serializar entre
 * abas: ao adquirir o lock, relê o storage e, se outra aba/chamada já renovou
 * (token válido + features presentes), reaproveita sem gastar o refresh token
 * rotativo de novo — evitando o 401 por "refresh token já consumido".
 *
 * @param staleToken token que disparou o 401 (path do interceptor). Se o token
 * em storage ainda for exatamente esse, força a renovação mesmo que o relógio
 * diga que não expirou (o servidor acabou de rejeitá-lo).
 */
const runRefresh = async (staleToken?: string): Promise<string> => {
  const currentAccess = localStorage.getItem("access_token");
  const refreshToken = localStorage.getItem("refresh_token");

  const cookieMode = authSession.isCookieMode();

  if (!cookieMode && (!currentAccess || !refreshToken)) {
    reportAuthEvent("auth_refresh_failed", {
      reason: "missing_tokens",
      will_logout: true,
      missing: !currentAccess ? "access_token" : "refresh_token",
    });
    throw new RefreshError("missing_tokens", true);
  }

  const expired = cookieMode
    ? authSession.isSessionExpired()
    : isExpired(currentAccess ?? "");
  const rejectedByServer =
    staleToken !== undefined && authSession.credentialMarker() === staleToken;
  const featuresMissing = featuresService.getFeatures().length === 0;

  const needsRefresh = expired || rejectedByServer || featuresMissing;

  if (!needsRefresh) {
    return currentAccess ?? "";
  }

  // Por que renovou: expirado pelo relógio local, rejeitado pelo servidor
  // (401 com token que o cliente achava válido = relógio adiantado/atrasado
  // ou sessão revogada) ou boot sem features em storage.
  const trigger = expired
    ? "expired"
    : rejectedByServer
      ? "rejected_by_server"
      : "features_missing";

  let response;
  try {
    const webClient = usesWebClient()
      ? {
          withCredentials: true,
          headers: { [WEB_CLIENT_HEADER]: WEB_CLIENT_VALUE },
        }
      : undefined;

    response = cookieMode
      ? await axios.post(
          `${env.API_URL}/v1/auth/cookie-refresh`,
          undefined,
          webClient
        )
      : await axios.post(
          `${env.API_URL}/v1/auth/refresh-token`,
          {
            access_token: currentAccess,
            refresh_token: refreshToken,
          },
          webClient
        );
  } catch (error) {
    const axiosError = error as AxiosError<ErrorApi>;
    const status = axiosError?.response?.status;
    // 404 no modo cookie é API anterior às rotas de cookie: sem Bearer para
    // voltar, a sessão não tem como continuar e a saída é o login.
    const shouldLogout =
      status === 401 || status === 403 || (cookieMode && status === 404);

    reportAuthEvent("auth_refresh_failed", {
      reason: status === undefined ? "network" : "http_error",
      trigger,
      status: status ?? null,
      code: axiosError?.code ?? null,
      api_message: axiosError?.response?.data?.message ?? null,
      will_logout: shouldLogout,
    });

    throw new RefreshError("refresh_request_failed", shouldLogout, status);
  }

  const { access_token, refresh_token, features, session } =
    response.data ?? {};

  if (!access_token && session?.transport !== "cookie") {
    reportAuthEvent("auth_refresh_failed", {
      reason: "invalid_response",
      trigger,
      status: response.status,
      will_logout: false,
    });
    throw new RefreshError("refresh_invalid_response", false);
  }

  await serverSetAccessToken(access_token ?? "", refresh_token, session);
  if (features) {
    featuresService.setFeatures(features);
  }

  reportAuthEvent("auth_refresh_ok", {
    trigger,
    rotated_refresh_token: Boolean(refresh_token),
  });

  return access_token ?? "";
};

/**
 * Ponto único de renovação compartilhado por boot (AuthProvider) e interceptor.
 * Garante uma só renovação em andamento por aba e coordena entre abas via lock.
 */
export const refreshAccessToken = (staleToken?: string): Promise<string> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const exec = async (): Promise<string> => {
    if (typeof navigator !== "undefined" && "locks" in navigator) {
      // `await` achata o Promise<Promise<string>> tipado por LockManager.request.
      return await navigator.locks.request("fidexa_token_refresh", () =>
        runRefresh(staleToken)
      );
    }
    return runRefresh(staleToken);
  };

  refreshPromise = exec().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

api.interceptors.request.use(async request => {
  if (typeof window !== "undefined") {
    if (usesWebClient()) {
      request.withCredentials = true;
      request.headers.set(WEB_CLIENT_HEADER, WEB_CLIENT_VALUE);
    }

    if (authSession.isCookieMode()) {
      (request as RequestWithMarker)._credentialMarker =
        authSession.credentialMarker();
      return request;
    }

    const accessToken = localStorage.getItem("access_token");
    if (accessToken) {
      request.headers.Authorization = `Bearer ${accessToken}`;
    }
  }
  return request;
});

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error instanceof AxiosError) {
      const status = error.response?.status;

      if (status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;

        const authHeader = originalRequest.headers?.Authorization as
          | string
          | undefined;
        const staleToken = authHeader?.startsWith("Bearer ")
          ? authHeader.slice(7)
          : (originalRequest as RequestWithMarker)._credentialMarker;

        // 401 com code "stale_token" (PermissionsVersionMiddleware do
        // NotiifyMe): o servidor está mandando renovar — as permissões
        // mudaram ou o usuário foi desativado. Garante a renovação forçada
        // mesmo se o header original não puder ser lido.
        const responseData = error.response?.data as ErrorApi | undefined;
        const isStaleToken = responseData?.code === "stale_token";
        const forcedStaleToken =
          staleToken ??
          (isStaleToken ? authSession.credentialMarker() : undefined);

        // O gate de sessão do NotiifyMe responde 401 em texto puro
        // ("Unauthorized"); o 401 de token expirado vem sem body. Registrar
        // o formato ajuda a distinguir os dois no PostHog.
        reportAuthEvent("auth_request_401", {
          path: originalRequest.url ?? null,
          method: originalRequest.method ?? null,
          code: responseData?.code ?? null,
          body_kind:
            typeof error.response?.data === "string"
              ? "text"
              : error.response?.data
                ? "json"
                : "empty",
          had_auth_header: Boolean(staleToken),
        });

        const oldFeatures = featuresService.getFeatures();

        try {
          const accessToken = await refreshAccessToken(forcedStaleToken);

          if (accessToken) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          } else {
            delete originalRequest.headers.Authorization;
          }

          // Permissões mudaram durante a sessão: recarrega para reaplicar o
          // estado de features de forma consistente em toda a árvore — mas
          // SÓ depois de reexecutar a request original: o 401 stale_token
          // pode ter caído em cima de uma mutation em voo, e recarregar sem
          // reenviar descartaria o trabalho do usuário.
          const featuresChanged = haveFeaturesChanged(
            oldFeatures,
            featuresService.getFeatures()
          );

          if (featuresChanged) {
            try {
              return await api(originalRequest);
            } finally {
              window.location.reload();
            }
          }

          return api(originalRequest);
        } catch (refreshError) {
          if (
            refreshError instanceof RefreshError &&
            refreshError.shouldLogout
          ) {
            await serverRemoveAccessToken("refresh_rejected", {
              source: "axios_interceptor",
              refresh_error: refreshError.message,
              refresh_status: refreshError.status ?? null,
              path: originalRequest.url ?? null,
            });
          }

          return Promise.reject(refreshError);
        }
      }
    }

    throw error;
  }
);

if (typeof window !== "undefined" && discardLegacySession()) {
  reportAuthEvent("auth_session_ended", { reason: "cookie_cutover" });
}

export { api };
